import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { Bell, Globe, LogOut } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Row, RowGroup, RowIcon, SectionHeader } from "@/components/patterns/row";
import { env } from "@/app/config/env";
import { isNative, platform } from "@/app/platform";
import { usePushRegistration } from "@/features/devices";
import { useAuthStatus, useSignOut } from "@/shared/auth";

export const Route = createFileRoute("/_app/settings")({
  component: SettingsScreen,
});

function SettingsScreen() {
  const { t, i18n } = useTranslation(["common", "devices"]);
  const navigate = useNavigate();
  const { user } = useAuthStatus();
  const signOut = useSignOut();
  const { state, enable } = usePushRegistration();

  const pushState = isNative ? state : "unsupported";

  return (
    <div className="flex flex-col gap-7">
      <h1 className="text-title">{t("common:nav.settings")}</h1>

      <RowGroup>
        <Row title={user?.name ?? user?.email ?? ""} description={user?.email} />
      </RowGroup>

      <div className="flex flex-col gap-2">
        <SectionHeader title={t("devices:push.title")} />
        <RowGroup>
          <Row
            leading={<RowIcon><Bell /></RowIcon>}
            title={t(`devices:push.state.${pushState}`)}
            description={t("devices:push.body")}
            // "failed" is retryable — permission was granted and the machinery missed.
            // "blocked" is not: only iOS Settings can undo a refusal.
            onSelect={
              pushState === "idle" || pushState === "failed" ? () => void enable() : undefined
            }
          />
        </RowGroup>
      </div>

      <div className="flex flex-col gap-2">
        <SectionHeader title={t("devices:language")} />
        <RowGroup>
          {env.locales.map((locale) => (
            <Row
              key={locale}
              leading={<RowIcon><Globe /></RowIcon>}
              title={locale.toUpperCase()}
              trailing={
                i18n.resolvedLanguage === locale ? (
                  <span className="text-support text-accent">✓</span>
                ) : undefined
              }
              onSelect={() => void i18n.changeLanguage(locale)}
            />
          ))}
        </RowGroup>
      </div>

      <Button
        variant="secondary"
        onClick={async () => {
          await signOut.mutateAsync();
          void navigate({ to: "/sign-in" });
        }}
      >
        <LogOut />
        {t("common:actions.signOut")}
      </Button>

      <p className="text-legal text-muted-foreground text-center tabular-nums">
        {env.appName} {env.version} · {env.appEnv} · {platform}
      </p>
    </div>
  );
}
